/**
 * Utilities for parsing markdown formatting from pasted text.
 * Used by TiptapField to handle paste events.
 */

/**
 * Convert inline markdown formatting to HTML tags.
 * Order matters: *** before ** before * to match the longest delimiter first.
 */
export const markdownToHtml = (line: string): string =>
  line
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>")
    .replace(/___(.+?)___/g, "<strong><em>$1</em></strong>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/__(.+?)__/g, "<strong>$1</strong>")
    .replace(/\*([^ *][^*]*?)\*(?!\*)/g, "<em>$1</em>")
    .replace(/_([^ _][^_]*?)_(?!_)/g, "<em>$1</em>");

/**
 * Convert plain text (with optional markdown formatting) to an HTML string
 * suitable for ProseMirror's DOMParser.
 *
 * - Double newlines become paragraph breaks (`<p>`)
 * - Single newlines become `<br>` (hard breaks)
 * - Inline markdown (bold/italic) is converted to `<strong>`/`<em>` tags
 */
export const plainTextToHtml = (text: string): string => {
  const normalized = text.replace(/\r\n/g, "\n");
  return normalized
    .split(/\n\n+/)
    .map((para) => {
      const inner = para
        .split(/\n/)
        .map((line) => markdownToHtml(line))
        .join("<br>");
      return `<p>${inner}</p>`;
    })
    .join("");
};

/**
 * Check if HTML contains actual formatting tags (not just wrapper divs/spans).
 */
export const hasRichFormatting = (html: string | null | undefined): boolean => {
  if (!html) return false;
  return /<(b|strong|i|em|u|s|strike|mark|sub|sup)\b/i.test(html);
};
